import { reduce } from 'lodash';
import { DateTime } from 'luxon';

import Theme from '@sonar/components/Theme';
import { formatDateToShort } from '@sonar/helpers/formatting';
import i18n from '@sonar/i18n';

/**
 * Legend displayed on the Dashboard chart for aggregated data.
 *
 * @param {Interval} interval - Luxon interval. The displayed legend is the selected period by the user
 *
 * @return {string}
 */
export const getAggregatedDataLabel = interval =>
  i18n.t('dashboard.kpi_chart.legend', {
    start: formatDateToShort(interval.s),
    end: formatDateToShort(interval.e),
  });

/**
 * Helper function that return the formatted period selected by the user
 *
 * @param {string} aggregate - 'hour', 'day, 'week',...
 * @param {Interval} interval - selected period
 *
 * @return {string}
 */
export const getPeriodLabel = (aggregate, interval) =>
  aggregate === 'hour' ? formatDateToShort(interval.s) : getAggregatedDataLabel(interval);

/**
 * Helper function that structures the input data for a chart of the Dashboard page
 * See https://vx-demo.now.sh/barstack
 *
 * @param {string} aggregate - 'hour', 'day, 'week',...
 * @param {Object} colorMapping - mapping between colors and data category (store or period)
 * @param {string} [currency] - currency code received by api
 * @param {[]|undefined} customersCounts - if present, data is for average spent. Needed to compute the weighted average for total
 * @param {Object} data - data of the selected period
 * @param {Interval} interval - selected period
 * @param {[]} [stores] - information about stores and which are selected
 * @param {string} [tax] - `tax_excluded` or `tax_included`
 *
 * @return {[]}
 */
export const createChartData = ({
  aggregate,
  colorMapping,
  currency,
  customersCounts,
  data,
  interval,
  stores = [],
  tax,
}) => {
  if (!Object.keys(data)) {
    return [];
  }

  const periodLabel = getPeriodLabel(aggregate, interval);

  const dataPerStore = Object.entries(data);

  const chartData = dataPerStore.reduce(
    (result, [storeId, { selected, reference }], storeIndex) => {
      const selectedValuesPerDate = selected ? Object.entries(selected.values).sort() : [];
      const referenceValuesPerDate = reference ? Object.entries(reference.values).sort() : null;

      const selectedValuesOfSingleStore = selectedValuesPerDate.reduce(
        (agg, [date, { value }], i) => {
          const store = stores.find(({ id }) => id === parseInt(storeId, 10));
          if (!store) {
            return agg;
          }

          const storeName = store.name;

          const selectedValue = tax ? value[tax] : value.customersCount;
          const referenceValue = referenceValuesPerDate
            ? tax
              ? referenceValuesPerDate[i][1].value[tax]
              : referenceValuesPerDate[i][1].value.customersCount
            : 0;

          return {
            ...agg,
            [date]: {
              [storeName]: selectedValue,
              [`${storeName}Evolution`]: getEvolution(selectedValue, referenceValue),
              [`${storeName}Color`]: colorMapping[storeName],
              [periodLabel]: selectedValue,
              reference: referenceValuesPerDate
                ? tax
                  ? referenceValuesPerDate[i][1].value[tax]
                  : referenceValuesPerDate[i][1].value.customersCount
                : 0,
            },
          };
        },
        {},
      );

      const selectedEntries = Object.entries(selectedValuesOfSingleStore);

      return selectedEntries.reduce((agg, [date, content], i) => {
        let selectedTotal;
        let referenceTotal;
        if (customersCounts) {
          // for average spent of all stores,the sum of all stores is not the sum of average spent
          // so we weight by the number of customers
          selectedTotal =
            (result[date] ? result[date][periodLabel] : 0) +
            content[periodLabel] *
              customersCounts[storeId].selected.values[date].value.customersCount;

          referenceTotal =
            (result[date] ? result[date].reference : 0) +
            content.reference *
              (customersCounts[storeId].reference
                ? Object.values(customersCounts[storeId].reference.values)[i].value.customersCount
                : 0);
        } else {
          selectedTotal = (result[date] ? result[date][periodLabel] : 0) + content[periodLabel];
          referenceTotal = (result[date] ? result[date].reference : 0) + content.reference;
        }

        const formattedData = {
          ...agg,
          [date]: {
            ...result[date],
            ...content,
            [periodLabel]: selectedTotal,
            reference: referenceTotal,
            evolution: getEvolution(selectedTotal, referenceTotal),
            ...(!agg[date] && {
              name: periodLabel,
              [`${periodLabel}Color`]: Theme.palette.chart.aggregated,
              currency,
              aggregate,
              from: date,
              to:
                i < selectedEntries.length - 1
                  ? DateTime.fromISO(selectedEntries[i + 1][0])
                      .minus(1)
                      .toISO()
                  : interval.e.toISO(),
            }),
          },
        };

        // compute evolution once we have accumulated entire values for selected and reference
        if (storeIndex === dataPerStore.length - 1) {
          if (customersCounts) {
            const totalSelectedCustomers =
              Object.values(customersCounts)
                .map(({ selected }) => selected)
                .reduce((acc, { values }) => acc + values[date].value.customersCount, 0) || 1;
            const totalReferenceCustomers =
              Object.values(customersCounts)
                .map(({ reference }) => reference)
                .reduce(
                  (acc, reference) =>
                    acc + (reference ? Object.values(reference.values)[i].value.customersCount : 0),
                  0,
                ) || 1;

            formattedData[date][periodLabel] /= totalSelectedCustomers;
            formattedData[date].reference /= totalReferenceCustomers;
          }
          formattedData[date].evolution = getEvolution(
            formattedData[date][periodLabel],
            formattedData[date].reference,
          );
        }

        return formattedData;
      }, {});
    },
    {},
  );

  return Object.values(chartData);
};

/**
 * Helper function that return the formatted period selected by the user
 * Types must be sorted by descending turnover
 *
 * @param {Object} data - turnover of stores by product type
 * @param {Array} colorMapping - association of each type to a color
 * @param {string} tax - `tax_excluded` or `tax_included`
 *
 * @return {import('@sonar/components/Charts/Widget/types').WidgetChartData[]}
 */
export const createProductTypeChart = (data, colorMapping, tax) => {
  if (!data) return [];
  let totalTurnover = 0;

  return Object.values(data)
    .reduce((data, store) => {
      const selectedTypes = store.selected?.values || {};

      Object.entries(selectedTypes).forEach(([type, value]) => {
        const amount = value.value[tax];

        totalTurnover += amount;
        const existingIndex = data.findIndex(d => d.type === type);

        if (existingIndex >= 0) {
          data[existingIndex] = {
            ...data[existingIndex],
            amount: data[existingIndex].amount + amount,
          };
        } else {
          const referenceTypes = store.reference?.values;
          const referenceAmount = referenceTypes?.[type]?.value[tax];
          data.push({
            amount,
            color: colorMapping[type],
            evolution: getEvolution(amount, referenceAmount || 0),
            type,
          });
        }
      });

      return data;
    }, [])
    .map(type => ({
      ...type,
      amount: type.amount,
      percent: type.amount / totalTurnover,
    }))
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Get evolution between selected and reference values
 * Ratio between reference and selected
 *
 * @param {number} selected - actual value from which we want the evolution
 * @param {number} reference - reference value to which the selected value is compared to
 *
 * @return {number}
 */
export const getEvolution = (selected, reference) =>
  reference > 0
    ? parseFloat(((selected - reference) / reference).toFixed(4))
    : selected > 0
    ? 1
    : 0;

/**
 *
 * @param {import('@sonar/modules/CurrentTills').ManyCurrentTillsInfo} tills
 * @param {import('@sonar/models').Period} period
 * @returns {import('@tiller/report-sdk').Limits}
 */
export const getEndDatesFromCurrentTills = (tills, period) => {
  return reduce(
    tills,
    (limitsAcc, { openDate }, storeId) => {
      const tillOpenDate = openDate && DateTime.fromISO(openDate, { zone: 'UTC' });
      const endDateLimit =
        tillOpenDate && tillOpenDate < period.interval.end ? tillOpenDate : period.interval.end;
      limitsAcc.push({ storeId: Number(storeId), endDateLimit });

      return limitsAcc;
    },
    [],
  );
};

/**
 * Set selected period + 1 day if end timeslot is superior to start timeslot
 * return period set with start / end timeslots
 *
 * @param {number} start - start timeslot
 * @param {number} end - end timeslot
 * @param {Period} period - period created from given period interval
 *
 * @return {void}
 */
export const setAdjustedPeriod = (start, end, period) => {
  // end of period is the day after the ending date but displayed period stays as is
  if (start >= end) {
    period.plus({ end: { days: 1 } });
  }

  period.set({
    start: { hours: start },
    end: { hours: end, minutes: 0, seconds: 0, milliseconds: 0 },
  });
};

export const getSalesDataOfSelectedStores = (entities, stores, storeIds) => {
  if (!Object.keys(entities).length || !stores.length || !storeIds.length) {
    return [];
  }

  return storeIds
    .map(storeId => {
      const { name } = stores.find(({ id }) => id === storeId);

      return { name, data: entities[storeId] || [] };
    })
    .reduce(
      (a, b) =>
        a.concat(
          b.data.map(product => ({
            store: b.name,
            ...product,
          })),
        ),
      [],
    );
};
